/**
 * Phase 3.28 — Dealer-Sicht: Eingehende Reservierungen auf eigene Inserate.
 * Zeigt Buyer-Daten (Name, E-Mail, Telefon), Listing-Snapshot, Countdown,
 * Vertrag-Versand-Status und Direkt-Chat-Link.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, CheckCircle2, XCircle, AlertCircle, FileSignature, Mail, Phone, MessageSquare, Bike, Download, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../lib/api";
import { previewPdfResponse, customerRecipient } from "../lib/pdfPreview";

function formatCountdown(ms) {
    if (ms <= 0) return "abgelaufen";
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h >= 24) {
        const d = Math.floor(h / 24);
        return `${d}t ${h % 24}h ${m}m`;
    }
    return `${h}h ${m}m`;
}

function statusInfo(r) {
    if (r.status === "completed") return { label: "Übergeben & verkauft", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: PackageCheck };
    if (r.status === "pending") {
        const expired = new Date(r.expires_at).getTime() < Date.now();
        if (expired) return { label: "Abgelaufen", cls: "bg-red-100 text-red-700 border-red-200", Icon: AlertCircle };
        return { label: "Aktiv", cls: "bg-green-100 text-green-700 border-green-200", Icon: CheckCircle2 };
    }
    if (r.status === "expired") return { label: "Abgelaufen", cls: "bg-gray-100 text-gray-700 border-gray-200", Icon: XCircle };
    if (r.status === "cancelled") return { label: "Storniert", cls: "bg-gray-100 text-gray-700 border-gray-200", Icon: XCircle };
    return { label: r.status, cls: "bg-gray-100 text-gray-600 border-gray-200", Icon: Clock };
}

export default function DealerReservationsList() {
    const [items, setItems] = useState(null);
    const [now, setNow] = useState(Date.now());
    const [completing, setCompleting] = useState(null);
    const [sendProtocol, setSendProtocol] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        try {
            const r = await api.get("/reservations/me/seller");
            setItems(r.data?.items || []);
        } catch {
            setItems([]);
        }
    };

    useEffect(() => {
        let alive = true;
        api.get("/reservations/me/seller")
            .then((r) => { if (alive) setItems(r.data?.items || []); })
            .catch(() => { if (alive) setItems([]); });
        return () => { alive = false; };
    }, []);

    const doComplete = async (r) => {
        setBusyId(r.id);
        try {
            await api.post(`/reservations/${r.id}/complete-handover`, { send_protocol: sendProtocol });
            toast.success(sendProtocol
                ? "Sofortkauf abgeschlossen — Fahrzeug verkauft, Übergabeprotokoll an den Käufer gesendet."
                : "Sofortkauf abgeschlossen — Fahrzeug als verkauft markiert.");
            setCompleting(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Abschluss fehlgeschlagen");
        } finally {
            setBusyId(null);
        }
    };

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(t);
    }, []);

    if (items === null) {
        return (
            <div className="space-y-3" data-testid="dealer-reservations-loading">
                {[1, 2].map((i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-sm h-28 animate-pulse" />
                ))}
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="dealer-reservations-empty">
                <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="font-display font-bold text-lg mb-1">Noch keine Reservierungen</h3>
                <p className="text-sm text-gray-600">
                    Sobald ein Kunde eines Ihrer Inserate reserviert, erscheint es hier mit Live-Countdown.
                </p>
            </div>
        );
    }

    const activeCount = items.filter((r) => r.status === "pending" && new Date(r.expires_at).getTime() > Date.now()).length;

    return (
        <div className="space-y-3" data-testid="dealer-reservations-list">
            <div className="flex items-center justify-between mb-2">
                <h2 className="font-display font-bold text-xl">Eingehende Reservierungen</h2>
                <div className="text-xs text-gray-500" data-testid="dealer-reservations-count">
                    {activeCount} aktiv · {items.length} gesamt
                </div>
            </div>
            {items.map((r) => {
                const info = statusInfo(r);
                const { Icon } = info;
                const remaining = new Date(r.expires_at).getTime() - now;
                const isActive = r.status === "pending" && remaining > 0;
                const isPaid = r.payment_status === "paid";
                const amtTxt = r.paid_amount_eur != null ? formatPrice(r.paid_amount_eur) : null;
                const listing = r.listing || {};
                const buyer = r.buyer || {};
                const cover = (listing.images && listing.images[0]) || null;
                return (
                    <div key={r.id} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`dealer-reservation-row-${r.id}`}>
                        <div className="flex flex-col sm:flex-row">
                            <Link to={`/listing/${r.listing_id}`} className="w-full sm:w-40 h-32 shrink-0 bg-gray-100 flex items-center justify-center" target="_blank" rel="noopener noreferrer">
                                {cover ? (
                                    <img src={cover} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <Bike className="w-10 h-10 text-gray-300" />
                                )}
                            </Link>
                            <div className="flex-1 p-4 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Inserat</div>
                                    <Link to={`/listing/${r.listing_id}`} className="font-bold text-sm hover:underline truncate block" target="_blank" rel="noopener noreferrer">
                                        {listing.title || `${listing.brand || ""} ${listing.model || ""}`.trim() || "Inserat"}
                                    </Link>
                                    <div className="text-xs text-gray-600 mt-0.5">
                                        {listing.year && <span>EZ {listing.year}</span>}
                                        {listing.price != null && (
                                            <span className="ml-2 font-mono-num font-bold">{formatPrice(listing.price)}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Käufer</div>
                                    <div className="font-bold text-sm truncate" data-testid={`dealer-reservation-buyer-${r.id}`}>
                                        {buyer.name || "Anonymer Käufer"}
                                    </div>
                                    {buyer.city && <div className="text-xs text-gray-600">{buyer.city}</div>}
                                    <div className="flex flex-wrap gap-2 mt-1 text-[11px]">
                                        {buyer.email && (
                                            <a href={`mailto:${buyer.email}`} className="inline-flex items-center gap-1 text-[#2563EB] hover:underline" data-testid={`dealer-reservation-email-${r.id}`}>
                                                <Mail className="w-3 h-3" /> E-Mail
                                            </a>
                                        )}
                                        {buyer.phone_mobile && (
                                            <a href={`tel:${buyer.phone_mobile}`} className="inline-flex items-center gap-1 text-[#2563EB] hover:underline" data-testid={`dealer-reservation-phone-${r.id}`}>
                                                <Phone className="w-3 h-3" /> Anrufen
                                            </a>
                                        )}
                                        <Link to={`/dashboard?tab=messages&buyer=${r.buyer_id}`} className="inline-flex items-center gap-1 text-[#2563EB] hover:underline" data-testid={`dealer-reservation-chat-${r.id}`}>
                                            <MessageSquare className="w-3 h-3" /> Chat
                                        </Link>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-bold ${info.cls}`} data-testid={`dealer-reservation-status-${r.id}`}>
                                        <Icon className="w-3 h-3" /> {info.label}
                                    </span>
                                    {isActive && (
                                        <div className="font-mono-num font-black text-sm text-green-700 mt-1" data-testid={`dealer-reservation-countdown-${r.id}`}>
                                            <Clock className="w-3 h-3 inline mr-1" />
                                            {formatCountdown(remaining)}
                                        </div>
                                    )}
                                    {r.contract_dispatched_at && (
                                        <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1 justify-end">
                                            <FileSignature className="w-3 h-3" /> Vertrag verschickt
                                        </div>
                                    )}
                                    <button
                                        onClick={async () => {
                                            try {
                                                const resp = await api.get(`/reservations/${r.id}/contract.pdf`, { responseType: "blob" });
                                                previewPdfResponse(resp, `Kaufvertrag_${r.id.slice(0, 8)}.pdf`, { recipient: customerRecipient(buyer) });
                                            } catch (_e) {
                                                alert("Kaufvertrag konnte nicht geladen werden.");
                                            }
                                        }}
                                        className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8] hover:underline"
                                        data-testid={`dealer-reservation-contract-pdf-${r.id}`}
                                        title="Vorausgefüllten Kaufvertrag als PDF herunterladen"
                                    >
                                        <Download className="w-3 h-3" /> Vorvertrag PDF
                                    </button>
                                    <div className="text-[10px] text-gray-500 mt-1">
                                        {new Date(r.reserved_at).toLocaleString("de-DE")}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {r.note && (
                            <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 text-xs" data-testid={`dealer-reservation-note-${r.id}`}>
                                <strong>Notiz vom Käufer:</strong> {r.note}
                            </div>
                        )}
                        {isPaid && (
                            <div className="px-4 py-3 bg-emerald-50 border-t border-emerald-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`dealer-reservation-paid-${r.id}`}>
                                <div className="inline-flex items-center gap-2 text-xs font-bold text-emerald-800">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Online bezahlt{amtTxt ? ` · ${amtTxt}` : ""}{r.payment_mode === "deposit" ? " (Anzahlung)" : ""}
                                </div>
                                {r.status === "pending" && (
                                    completing === r.id ? (
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`dealer-handover-panel-${r.id}`}>
                                            <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer">
                                                <input type="checkbox" checked={sendProtocol} onChange={(e) => setSendProtocol(e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" data-testid={`dealer-handover-protocol-${r.id}`} />
                                                Übergabeprotokoll an Käufer senden
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => doComplete(r)}
                                                    disabled={busyId === r.id}
                                                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold uppercase tracking-wider rounded-sm disabled:opacity-60"
                                                    data-testid={`dealer-handover-confirm-${r.id}`}
                                                >
                                                    {busyId === r.id ? "Wird abgeschlossen…" : "Bestätigen"}
                                                </button>
                                                <button onClick={() => setCompleting(null)} disabled={busyId === r.id} className="text-[11px] text-gray-500 hover:text-gray-800" data-testid={`dealer-handover-cancel-${r.id}`}>
                                                    Abbrechen
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => { setCompleting(r.id); setSendProtocol(true); }}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#111827] hover:bg-black text-[#FFCC00] text-[11px] font-bold uppercase tracking-wider rounded-sm"
                                            data-testid={`dealer-complete-handover-btn-${r.id}`}
                                        >
                                            <PackageCheck className="w-3.5 h-3.5" /> Übergabe abschließen & verkauft
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
